




















































import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  set,
  toRefs,
  useFetch,
  useRoute
} from '@nuxtjs/composition-api'
import CDraggable from '~/components/shared/configurable/CDraggable.vue'
import { useDep } from '~/compositions/dependency-container'
import ShortVideosService from '~/services/ShortVideosService'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import { handleError } from '~/utils/http'
import { useSnackbar } from '~/compositions/snackbar'
import { useLogger } from '~/compositions/logger'
import { useI18n } from '~/compositions/i18n'
import { AxiosError } from 'axios'
import CShortVideoUploaderForm from '~/components/shared/configurable/file/CShortVideoUploaderForm.vue'
import Title from '~/components/car/classifieds/view/partials/Header/Title.vue'
import CPrice from '~/components/shared/configurable/CPrice.vue'
import CVideoPlayer from '~/components/shared/configurable/video/CVideoPlayer.vue'
import {
  ShortVideo,
  ShortVideoStatus,
  ShortVideosData,
  ShortsClassified
} from '~/models/short-video'
import { defineComponentTranslations } from '~/utils/i18n'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons'
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit'
import { HttpStatus } from '~/constants/http'
import ShortVideosPendingThumb from '~/components/shared/short-videos/ShortVideosPendingThumb.vue'
import CConfirmationModal from '~/components/shared/configurable/modal/CConfirmationModal.vue'
import { useNamespacedStore } from '~/compositions/store'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'

export default defineComponent({
  components: {
    CShortVideoUploaderForm,
    CDraggable,
    CImg,
    Title,
    CPrice,
    CVideoPlayer,
    ShortVideosPendingThumb,
    CConfirmationModal
  },
  props: {
    inPage: {
      type: Boolean,
      default: false
    },
    classifiedHidden: {
      type: Boolean,
      default: false
    },
    classifiedId: {
      type: Number,
      default: null
    }
  },
  setup(props, { emit }) {
    const { inPage, classifiedId } = toRefs(props)
    const route = useRoute()
    const shortVideos = ref<ShortVideosData>()
    const uploadStart = ref(false)
    const fileUploaderRef = ref(null)
    const isUploaderVisible = ref(true)
    const videoDescription = ref('')
    const isLoading = ref(false)
    const hasChanges = ref(false)
    const isUploadVideoLoading = ref(false)
    const selectedVideo = ref<ShortVideo>()
    const { t } = useI18n()
    const snackbar = useSnackbar()
    const logger = useLogger()
    const classified = ref<ShortsClassified>()
    const progress = ref(0)
    const responseError = ref<number | null>(null)
    const pendingInterval = ref(null)
    const videoId = ref<number | null>(null)
    const deleteVideoData = ref<{ id: number; status: ShortVideoStatus }>()
    const isDeleteVideoConfirmationVisible = ref<boolean>(false)

    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const isAnon = computed(() => userGetters('isAnon'))

    const { fetch } = useFetch(async () => {
      const cid = route.value.params?.cid || classifiedId.value
      if (cid) {
        try {
          const {
            shorts,
            classified: classifiedData
          } = await shortVideosService.fetchClassifiedShortVideos(cid)
          shortVideos.value = shorts
          classified.value = classifiedData
          if (shorts.pending.length === 0 && pendingInterval.value) {
            clearInterval(pendingInterval.value)
          }
        } catch (error) {
          if (error?.response?.status) {
            responseError.value = error.response.status
          }

          handleError(error as AxiosError, snackbar, logger, t)
        }
      } else if (!inPage.value) {
        shortVideos.value = {
          pending: [],
          hidden: [],
          public: [],
          error: []
        }
      }
    })

    const icons = {
      leftArrow: faChevronLeft,
      edit: faEdit
    }

    const shortVideosService = useDep(ShortVideosService)

    onMounted(() => {
      createInterval()
    })
    onUnmounted(() => {
      if (pendingInterval.value) {
        clearInterval(pendingInterval.value)
      }
    })

    const acceptedFileTypes = [
      'video/mp4',
      'video/x-matroska',
      'video/webm',
      'video/quicktime' // apple mime type
    ]

    function handleUploadCompletedSuccessfuly(pendingVideo: ShortVideo) {
      selectedVideo.value = pendingVideo
      if (!shortVideos.value.pending.find(p => p.id === pendingVideo.id)) {
        shortVideos.value.pending.push(pendingVideo)
      }
    }

    function handleUploadFailed() {
      isLoading.value = false
      if (fileUploaderRef.value) {
        fileUploaderRef.value.closeUploaderModal()
      }
    }

    function handleClickCancel() {
      videoDescription.value = ''
      isUploaderVisible.value = true
      selectedVideo.value = undefined
    }
    async function handleClickSave({
      video,
      description
    }: {
      video: ShortVideo
      description: string
    }) {
      if (video) {
        selectedVideo.value = video
      }
      if (isAnon.value || !classified.value) {
        return
      }
      try {
        isLoading.value = true
        const { short } = await shortVideosService.editShortVideo(
          selectedVideo.value.id,
          {
            forClassifiedId: selectedVideo.value?.classified?.id,
            description,
            tags: selectedVideo.value?.tags
          }
        )

        const index = shortVideos.value[short.status].findIndex(
          (s: ShortVideo) => s.id === selectedVideo.value?.id
        )
        set(shortVideos.value[short.status], index, short)

        if (hasChanges.value) {
          snackbar.success(t('your changes were saved'))
          hasChanges.value = false
        }
      } catch (error) {
        handleError(error as AxiosError, snackbar, logger, t)
      } finally {
        isLoading.value = false
      }
    }

    async function handleAddFile({
      file,
      duration,
      coverUrl
    }: {
      file: File
      duration: number
      coverUrl: string
    }) {
      isLoading.value = true

      let pendingVideo = {} as ShortVideo
      try {
        await shortVideosService.uploadShortVideo(
          file,
          (p: number) => (progress.value = p),
          (data: ShortVideo) => {
            if (data) {
              pendingVideo = data
              videoId.value = data.id
              handleUploadCompletedSuccessfuly({
                ...pendingVideo,
                thumbnail: coverUrl,
                extras: { duration }
              })
            }
          },
          classified.value?.id
        )
        isLoading.value = false
        uploadStart.value = false
        emit('upload-complete', pendingVideo)
        createInterval()
      } catch (error) {
        handleUploadFailed()
        const index = shortVideos.value.pending.findIndex(
          p => p.id === pendingVideo.id
        )
        if (Number.isFinite(index)) {
          set(shortVideos.value.pending, index, {
            ...shortVideos.value.pending[index],
            thumbnail: ''
          })
        }

        if (error?.message?.match(/failed to/)) {
          snackbar.error(t('upload_failure'))
          return
        }
        handleError(error as AxiosError, snackbar, logger, t)
      } finally {
        progress.value = 0
        isUploadVideoLoading.value = false
      }
    }

    function handleDeleteVideo(videoData: {
      id: number
      status: ShortVideoStatus
    }) {
      deleteVideoData.value = videoData
      setTimeout(() => (isDeleteVideoConfirmationVisible.value = true), 100)
    }

    async function handleDeleteOk(videoId: number = null) {
      const vId = videoId || deleteVideoData.value.id
      try {
        const status = videoId ? 'pending' : deleteVideoData.value?.status
        const video = shortVideos.value?.[status]?.find(
          (pv: ShortVideo) => pv.id === vId
        ) as ShortVideo
        if (video?.id) {
          const { message } = await shortVideosService.deleteVideo(video.id)
          if (!videoId) {
            snackbar.success(message)
          }
          const filteredVideos = shortVideos.value[video.status].filter(
            (v: ShortVideo) => v.id !== video.id
          )
          set(shortVideos.value, video.status, filteredVideos)
          emit('delete', video)
        }
        if (shortVideos.value?.pending.length === 0) {
          clearInterval(pendingInterval.value)
        }
      } catch (error) {
        handleError(error as AxiosError, snackbar, logger, t)
      }
    }

    async function handleOrderChange(publicVideos: ShortVideo[]) {
      if (!classified.value) {
        emit('order-change', publicVideos)
        return
      }
      try {
        const filteredVideos = publicVideos.filter(
          pp => pp.status === ShortVideoStatus.PUBLIC
        )
        await shortVideosService.updateOrder(
          classified.value?.id as number,

          filteredVideos.map(pp => pp.id)
        )
        shortVideos.value.public = filteredVideos
      } catch (error) {
        handleError(error as AxiosError, snackbar, logger, t)
      }
    }

    function handleInputChange() {
      hasChanges.value = true
    }
    function createInterval() {
      if (shortVideos.value?.pending?.length > 0 && classified.value) {
        pendingInterval.value = setInterval(() => fetch(), 60000)
      }
    }
    function handleCancelUpload() {
      shortVideosService.cancelUpload()
      if (!isAnon.value) {
        handleDeleteOk(videoId.value)
      }
      snackbar.success(t('cancelled_upload'))
      handleUploadFailed()
      progress.value = 0
    }
    return {
      fileUploaderRef,
      isUploaderVisible,
      videoDescription,
      handleUploadCompletedSuccessfuly,
      handleUploadFailed,
      isLoading,
      handleClickSave,
      selectedVideo,
      shortVideos,
      icons,
      classified,
      handleDeleteVideo,
      progress,
      handleAddFile,
      handleDeleteOk,
      handleOrderChange,
      isDeleteVideoConfirmationVisible,
      handleInputChange,
      HttpStatus,
      responseError,
      handleCancelUpload,
      acceptedFileTypes,
      ShortVideoStatus,
      handleClickCancel,
      isAnon
    }
  },
  i18n: defineComponentTranslations({
    cancelled_upload: {
      en: 'Τhe file upload was cancelled',
      el: 'Η μεταφόρτωση αρχείου ακυρώθηκε'
    },
    upload_failure: {
      en: 'File upload failure',
      el: 'Αποτυχία μεταφόρτωσης αρχείων'
    },
    hidden_classified_msg: {
      en: 'You cannot upload a video to a hidden classified',
      el: 'Δεν μπορείς να ανεβάσεις βίντεο σε κρυμμένη αγγελία'
    }
  })
})
